import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { CircularProgress } from 'src/components/CircularProgress';
import { useOptionalSession } from 'src/contexts/SessionContext';
import { fetchAllPlatforms } from 'src/models/platform/platform';
import { platformLabel } from 'src/models/platform/utils';
import {
  fetchPlatformNodes,
  platformNodesQueryKey,
} from 'src/models/platform-node/platform-node';
import SiteSelect from 'src/shared/Forms/Selects/SiteSelect';

interface SiteFilterProps {
  value?: string[] | string | number;
  setValue: (value?: string[]) => void;
  valueOnly?: boolean;
  showMyLocationsOnly?: boolean;
}

export const SiteFilter: React.FC<SiteFilterProps> = ({
  value,
  setValue,
  valueOnly,
  showMyLocationsOnly,
}) => {
  const { session } = useOptionalSession();
  const { data: platforms, isLoading: isLoadingPlatforms } = useQuery({
    queryKey: ['platforms'],
    queryFn: async () => {
      const response = await fetchAllPlatforms();

      return response || [];
    },
    enabled: !!session?.id,
    staleTime: 60000,
    gcTime: 60000,
  });
  const { data: nodes = { nodes: [], total: 0 }, isLoading: isLoadingNodes } =
    useQuery({
      queryKey: [platformNodesQueryKey],

      queryFn: () =>
        fetchPlatformNodes({
          includeHierarchyNodes: true,
          fetchAll: true,
        }),

      enabled: !!session?.id,
    });

  const parsedValue = useMemo(() => {
    if (Array.isArray(value)) {
      return value;
    } else if (typeof value === 'string') {
      return value?.split(',');
    } else if (value) {
      return [value.toString()];
    }
    return [];
  }, [value]);

  const sitesLabels = useMemo(() => {
    return parsedValue?.map((v) => {
      if (v.includes('node')) {
        const node = nodes.nodes.find((node) => node.id == v.split(' ')[0]);
        return node?.name + ' (' + node?.hierarchyNode?.name + ')';
      }
      const platform = platforms?.find((platform) => platform.id == v);
      return platformLabel(platform);
    });
  }, [nodes, platforms, parsedValue]);

  if (isLoadingPlatforms || isLoadingNodes) {
    return <CircularProgress color="inherit" size="small" sx={{ p: '3px' }} />;
  }

  if (valueOnly) {
    return <>{sitesLabels?.join(', ') || ''}</>;
  }
  return (
    <SiteSelect
      values={parsedValue || []}
      onChange={(value) => setValue(value as string[])}
      showMyLocationsOnly={showMyLocationsOnly}
    />
  );
};
